import i18n from '@/lib/i18n';

/**
 * Plain-language copy for voice/call error codes (backend 96xxx block).
 *
 * Same doctrine as `workflow-error-copy.ts`: the backend returns a code plus
 * safe context and never writes end-user prose, so every friendly sentence
 * lives here. That split is what fixed the leak where a failed dial toasted
 * "Voice calling is not enabled for organization <uuid>" — the exception now
 * carries the org id in `details` and this map supplies the words.
 *
 * The English strings are i18n *defaults*: `voiceErrorMessage()` resolves
 * `voice.errors.<code>` and falls back to them, so an untranslated locale
 * renders English rather than a raw key.
 */
const COPY: Record<number, string> = {
    96000: 'Voice calling is not enabled for your organization. Contact your administrator to turn it on.',
    96001: 'This phone number is not enabled for voice calls. Enable it in Settings → Voice → Numbers.',
    96002: 'No voice-enabled number is available to call from. Ask your administrator to enable one for you.',
    96003: 'Voice calling needs to be re-enabled after your messaging provider changed. Ask your administrator to turn it back on in Settings → Voice.',
    96004: "You're set to Offline, so calls can't be placed. Change your status to Active in the call widget.",
    96010: 'That call could not be found. It may have already ended.',
    96011: 'That action is not available for the call right now.',
    96012: "You are not a participant of this call, so you can't change it.",
    96013: 'The call is no longer active.',
    96020: 'We could not reach your calling provider. Check the connection in Settings → Channels, then try again.',
    96030: 'The recording is still being processed. Try again in a moment.',
};

interface IApiErrorEnvelope {
    readonly error?: { readonly errorCode?: number } | string;
}

/**
 * Friendly copy for a voice error response, or `undefined` when the payload
 * is not one of ours — callers then fall through to their own fallback, the
 * same contract as `friendlyEntitlementMessage`.
 */
export function voiceErrorMessage(data: unknown): string | undefined {
    if (!data || typeof data !== 'object') return undefined;
    const { error } = data as IApiErrorEnvelope;
    if (!error || typeof error !== 'object') return undefined;

    const { errorCode } = error;
    if (typeof errorCode !== 'number') return undefined;

    const fallback = COPY[errorCode];
    if (!fallback) return undefined;

    return i18n.t(`voice.errors.${String(errorCode)}`, fallback);
}
